import { inject, Injectable } from '@angular/core';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    MATERIAL_SOURCE_CODE,
    MaterialModel,
    MaterialService,
    MaterialUtilitiesService,
    NotificationDetailState,
    NotificationModel,
    ParticipantModel,
    Region,
    selectAppsTaskCode,
    selectByBoxKonnectLink,
    selectCBDocLink,
    selectCSMLTrackerLink,
    selectDisableQuoteForNotificationTypes,
    selectDisplayDocBox,
    selectDisplayMaterialPricing,
    selectDisplayMaterialQuote,
    selectEnableOtherTasks,
    selectSeSoDivisions,
    selectSeSoLink,
    selectShowParticipantList,
    selectTeamplayFleetLink,
    selectUseCsmlUrlWithParam,
    stateToNotificationIdModel,
    UriUtilitiesService,
    UserMasterInstanceToken,
    UserMasterInterface,
} from '@evorait/pace-core';
import {
    PaceActionLink,
    PaceActionLinkGroup,
    PaceNotificationActionLinks,
    PaceNotificationCarouselItem,
    PaceNotificationDetailCarousel,
    PaceNotificationLongtext,
    PaceNotificationMaterialItem,
    PaceNotificationParticipantItem,
} from '@evorait/pace-components';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

/** How many carousel columns fit next to each other per screen size. */
const COLUMNS_FOR_REGION: Record<Region, number> = {
    Unknown: 1,
    SmallRegion: 2,
    MediumRegion: 2,
    LargeRegion: 3,
    DesktopRegion: 4,
    LargeDesktopRegion: 4,
};

/** A participant column holds two entries; a material column only one. */
const PARTICIPANTS_PER_COLUMN = 2;

/** More than this many action-link groups and the panel splits the last one into two columns. */
const MAX_NUM_OF_LINKS_PER_COL = 3;

/** Which action an action-links button stands for — the contract with the screen handling it. */
export enum NOTIFICATION_DETAIL_ACTION {
    MAINTENANCE_LOCATION = 'maintenanceLocation',
    SYSTEM_CONFIGURATION = 'systemConfiguration',
    EQUIPMENT_HISTORY = 'equipmentHistory',
    EQUIPMENT_CONFIGURATION = 'equipmentConfiguration',
    EQUIPMENT_OTHER_TASKS = 'equipmentOtherTasks',
    MATERIAL_REQUEST = 'materialRequest',
    MATERIAL_QUOTE = 'materialQuote',
    MATERIAL_PRICING = 'materialPricing',
    DOC_BOX = 'docBox',
}

/** Which external links the panel offers. Only used to key the rendered rows. */
enum NOTIFICATION_DETAIL_LINK {
    TEAMPLAY_FLEET = 'teamplayFleet',
    SERVICE_SOFTWARE_ONLINE = 'serviceSoftwareOnline',
    BY_BOX_KONNECT = 'byBoxKonnect',
    CB_DOC = 'cbDoc',
}

/**
 * Turns the notification store's {@link NotificationDetailState} into the view models the
 * presentational long-text, carousel and action-links components consume.
 *
 * Everything the store slice deliberately leaves out is added here: translations, the material
 * domain, the config- and client-dependent link targets, and the screen-size-dependent carousel
 * layout. Same split {@link DashboardNotificationCardFactory} follows for the dashboard card.
 */
@Injectable({ providedIn: 'root' })
export class NotificationDetailsContentFactory {
    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly translate = inject(TranslateService);
    private readonly clientManager = inject<ClientManagerInterface>(ClientManagerInstanceToken);
    private readonly userMasterService = inject<UserMasterInterface>(UserMasterInstanceToken);
    private readonly materialService = inject(MaterialService);
    private readonly materialUtilitiesService = inject(MaterialUtilitiesService);
    private readonly uriUtilities = inject(UriUtilitiesService);
    private readonly store = inject(Store);

    private readonly enableOtherTasks = this.store.selectSignal(selectEnableOtherTasks);
    private readonly appsTaskCode = this.store.selectSignal(selectAppsTaskCode);
    private readonly displayMaterialPricingEnabled = this.store.selectSignal(selectDisplayMaterialPricing);
    private readonly displayMaterialQuoteEnabled = this.store.selectSignal(selectDisplayMaterialQuote);
    private readonly teamplayFleetLink = this.store.selectSignal(selectTeamplayFleetLink);
    private readonly seSoLink = this.store.selectSignal(selectSeSoLink);
    private readonly seSoDivisions = this.store.selectSignal(selectSeSoDivisions);
    private readonly cbDocLink = this.store.selectSignal(selectCBDocLink);
    private readonly byBoxKonnectLink = this.store.selectSignal(selectByBoxKonnectLink);
    private readonly displayDocBox = this.store.selectSignal(selectDisplayDocBox);
    private readonly csmlTrackerLink = this.store.selectSignal(selectCSMLTrackerLink);
    private readonly useCsmlUrlWithParam = this.store.selectSignal(selectUseCsmlUrlWithParam);

    /**
     * @param notification the store-built model owning the parsed long texts
     * @param isMobile whether the screen folds the pre-clarification away
     */
    public createLongtext(notification: NotificationModel, isMobile: boolean): PaceNotificationLongtext {
        return {
            title: <string>this.translate.instant('NOTIFICATION_DETAILS_LONG_TEXT_INFO_NOTIFICATION_TITLE'),
            preclarificationTitle: <string>(
                this.translate.instant('NOTIFICATION_DETAILS_LONG_TEXT_INFO_PRE_CLARIFICATION_TITLE')
            ),
            showMoreLabel: <string>this.translate.instant('NOTIFICATION_DETAILS_LONG_TEXT_SHOW_MORE'),
            showLessLabel: <string>this.translate.instant('NOTIFICATION_DETAILS_LONG_TEXT_SHOW_LESS'),
            linesHtml: notification.notificationLongtext.map((line) => line.TextLine),
            preclarificationLinesHtml: notification.preclarificationLongtext.map((line) => line.TextLine),
            isPreclarificationCollapsible: isMobile,
        };
    }

    /**
     * The carousel: the notification's participants where the task group calls for a participant
     * list, its ordered materials otherwise.
     *
     * @param state the notification's detail state
     * @param notification the store-built model owning the participant models
     * @param region the screen size deciding how many columns a slide holds
     */
    public createCarousel(
        state: NotificationDetailState,
        notification: NotificationModel,
        region: Region,
    ): PaceNotificationDetailCarousel {
        const showParticipants = this.store.selectSignal(selectShowParticipantList(state.taskGroup))();
        const items = showParticipants
            ? notification.participantSet.map((participant) => this.toParticipantItem(participant))
            : this.getOrderedMaterials(state).map((material) => this.toMaterialItem(state, material));

        return {
            title: showParticipants
                ? <string>this.translate.instant('NOTIFICATION_DETAILS_PARTICIPANTLIST_TITLE')
                : <string>this.translate.instant('NOTIFICATION_DETAILS_MATERIAL_TITLE'),
            count: `(${items.length})`,
            trackAllOrdersUrl: showParticipants || items.length === 0 ? '' : this.getCsmlTrackerUrl(state),
            trackAllOrdersLabel: <string>this.translate.instant('TRACK_ALL_ORDERS'),
            emptyText: <string>this.translate.instant('NOTIFICATION_DETAILS_NO_RECORDS_AVAILABLE_TITLE'),
            slides: this.toSlides(items, COLUMNS_FOR_REGION[region], showParticipants ? PARTICIPANTS_PER_COLUMN : 1),
        };
    }

    /**
     * The action-links panel. Which groups appear, and how wide they are, depends on the user and
     * the notification: the material group is dropped for an application specialist, and the
     * external-links group only appears when there is at least one link to show.
     *
     * @param state the notification's detail state
     */
    public createActionLinks(state: NotificationDetailState): PaceNotificationActionLinks {
        const externalLinks = this.getExternalLinks(state);
        const hasLinksGroup = externalLinks.length > 0;
        const splitsLinksGroup = externalLinks.length > MAX_NUM_OF_LINKS_PER_COL;
        const standardColumnClass = hasLinksGroup ? 'col-12 col-sm-6 col-md-3' : 'col-12 col-sm-6 col-md-4';

        const groups: PaceActionLinkGroup[] = [
            {
                id: 'dataMaintenance',
                title: <string>this.translate.instant('NOTIFICATION_DETAILS_DETAIL_LINKS_DATA_MAINTENANCE_TITLE'),
                columnClass: standardColumnClass,
                wrapsIntoColumns: false,
                links: [
                    this.action(
                        NOTIFICATION_DETAIL_ACTION.MAINTENANCE_LOCATION,
                        'NOTIFICATION_DETAILS_DETAIL_LINKS_MAINTENANCE_LOCATION',
                    ),
                    this.action(
                        NOTIFICATION_DETAIL_ACTION.SYSTEM_CONFIGURATION,
                        'NOTIFICATION_DETAILS_DETAIL_LINKS_MAINTENANCE_SYSTEM_CONFIG',
                    ),
                ],
            },
            {
                id: 'equipment',
                title: <string>this.translate.instant('NOTIFICATION_DETAILS_DETAIL_LINKS_EQUIPMENTS_TITLE'),
                columnClass: standardColumnClass,
                wrapsIntoColumns: false,
                links: [
                    this.action(
                        NOTIFICATION_DETAIL_ACTION.EQUIPMENT_HISTORY,
                        'NOTIFICATION_DETAILS_DETAIL_LINKS_EQUIPMENT_HISTORY',
                    ),
                    this.action(
                        NOTIFICATION_DETAIL_ACTION.EQUIPMENT_CONFIGURATION,
                        'NOTIFICATION_DETAILS_DETAIL_LINKS_EQUIPMENT_SUB_EQUIPMENT',
                    ),
                ],
            },
        ];

        if (this.enableOtherTasks() && !this.userMasterService.isPaceOnlineUser()) {
            groups[1].links.push(
                this.action(
                    NOTIFICATION_DETAIL_ACTION.EQUIPMENT_OTHER_TASKS,
                    'NOTIFICATION_DETAILS_DETAIL_LINKS_EQUIPMENT_OTHER_TASKS',
                ),
            );
        }

        if (state.taskGroup !== this.appsTaskCode()) {
            groups.push({
                id: 'material',
                title: <string>this.translate.instant('NOTIFICATION_DETAILS_DETAIL_LINKS_MATERIAL_TITLE'),
                columnClass: splitsLinksGroup ? 'col-12 col-sm-6 col-md-2' : standardColumnClass,
                wrapsIntoColumns: false,
                links: this.getMaterialLinks(state),
            });
        }

        if (hasLinksGroup) {
            groups.push({
                id: 'links',
                title: <string>this.translate.instant('NOTIFICATION_DETAILS_DETAIL_LINKS_LINKS_TITLE'),
                columnClass: splitsLinksGroup ? 'col-12 col-sm-6 col-md-4' : 'col-12 col-sm-6 col-md-3',
                wrapsIntoColumns: true,
                links: externalLinks,
            });
        }

        return { groups };
    }

    /**
     * The materials the carousel shows, which the pickup dialog needs to look one up again.
     *
     * @param state
     */
    public getOrderedMaterials(state: NotificationDetailState): MaterialModel[] {
        return this.materialService
            .getAllMaterialsForNotification(stateToNotificationIdModel(state.notificationId))
            .filter(
                (material) =>
                    material.materialSource.sourceCode === MATERIAL_SOURCE_CODE.ORDERED &&
                    material.showOnNotificationDetail,
            );
    }

    /**
     * An action rendered as a button: no target, so the panel reports the selection back.
     *
     * @param id the action's id
     * @param labelKey translation key of its label
     * @param isDisabled whether the action is currently unavailable
     * @returns the action link
     */
    private action(id: NOTIFICATION_DETAIL_ACTION, labelKey: string, isDisabled = false): PaceActionLink {
        return { id, label: <string>this.translate.instant(labelKey), url: '', isDisabled };
    }

    /**
     * The material group's actions: the request always, quotes and pricing where configured.
     *
     * @param state the notification's detail state
     * @returns the actions of the material group
     */
    private getMaterialLinks(state: NotificationDetailState): PaceActionLink[] {
        const links = [
            this.action(
                NOTIFICATION_DETAIL_ACTION.MATERIAL_REQUEST,
                'NOTIFICATION_DETAILS_DETAIL_LINKS_MATERIAL_REQUEST',
                this.materialService.isMaterialRequested(stateToNotificationIdModel(state.notificationId)),
            ),
        ];
        if (this.displayMaterialQuote(state)) {
            links.push(
                this.action(
                    NOTIFICATION_DETAIL_ACTION.MATERIAL_QUOTE,
                    'NOTIFICATION_DETAILS_DETAIL_LINKS_MATERIAL_QUOTE',
                ),
            );
        }
        if (this.displayMaterialPricing(state)) {
            links.push(
                this.action(
                    NOTIFICATION_DETAIL_ACTION.MATERIAL_PRICING,
                    'NOTIFICATION_DETAILS_DETAIL_LINKS_MATERIAL_PRICING',
                ),
            );
        }
        return links;
    }

    /**
     * Material pricing is off for Austrian inVitro notifications: sales org and plant are the same
     * for both processes there, and only inVivo may see pricing.
     *
     * @param state the notification's detail state
     */
    public displayMaterialPricing(state: NotificationDetailState): boolean {
        const isAustriaInvitro =
            this.clientManager.isNgmP58Austria() &&
            state.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO;
        return this.displayMaterialPricingEnabled() && !isAustriaInvitro;
    }

    /**
     * Material quotes are configured per client, and for Finland additionally allowed for inVitro
     * notifications — unless the notification's type has quotes disabled outright.
     *
     * @param state the notification's detail state
     */
    public displayMaterialQuote(state: NotificationDetailState): boolean {
        const isFinlandInvitro =
            this.clientManager.isNgmP58Finland() &&
            state.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO;
        return (
            (this.displayMaterialQuoteEnabled() || isFinlandInvitro) &&
            !this.store.selectSignal(selectDisableQuoteForNotificationTypes(state.notificationType))()
        );
    }

    /**
     * The external links and the DocBox action, in the order they are rendered. Each one only appears
     * where it is both configured and applicable to the notification's equipment.
     *
     * @param state the notification's detail state
     * @returns the links and actions of the links group
     */
    private getExternalLinks(state: NotificationDetailState): PaceActionLink[] {
        const links: PaceActionLink[] = [];
        const teamplayFleetLink = this.clientManager.isNgmP58() ? this.teamplayFleetLink() : '';
        const serviceSoftwareOnlineLink = this.seSoLink();
        const cbDocLink = this.cbDocLink();
        const byBoxKonnectLink = this.byBoxKonnectLink();
        const equipment = state.mainEquipment;

        if (equipment && teamplayFleetLink.length > 0) {
            const countryLength = 2;
            const country = state.salesOrg.substring(0, countryLength);
            links.push({
                id: NOTIFICATION_DETAIL_LINK.TEAMPLAY_FLEET,
                label: 'teamplay Fleet',
                url: `${teamplayFleetLink}?materialNo=${equipment.matnr}&serialNo=${equipment.sernr}&country=${country}`,
                isDisabled: false,
            });
        }
        if (equipment && serviceSoftwareOnlineLink.length > 0 && this.seSoDivisions().includes(equipment.division)) {
            links.push({
                id: NOTIFICATION_DETAIL_LINK.SERVICE_SOFTWARE_ONLINE,
                label: 'SeSO',
                url: `${serviceSoftwareOnlineLink}${equipment.matnr}/${equipment.sernr}`,
                isDisabled: false,
            });
        }
        if (this.displayDocBox()) {
            links.push(this.action(NOTIFICATION_DETAIL_ACTION.DOC_BOX, 'NOTIFICATION_DETAILS_DETAIL_LINKS_DOC_BOX'));
        }
        if (byBoxKonnectLink.length > 0) {
            links.push({
                id: NOTIFICATION_DETAIL_LINK.BY_BOX_KONNECT,
                label: 'ByBox Konnect',
                url: byBoxKonnectLink,
                isDisabled: false,
            });
        }
        if (cbDocLink.length > 0) {
            const url = this.getCbDocUrl(cbDocLink, equipment?.matnr ?? '');
            if (url) {
                links.push({ id: NOTIFICATION_DETAIL_LINK.CB_DOC, label: 'CB-Doc', url, isDisabled: false });
            }
        }
        return links;
    }

    /**
     * The CB-Doc link has the material number substituted into it. A configured link without the
     * placeholder is a configuration error and yields no link, but it still counts towards the column
     * layout — which is why the count is taken before this can fail.
     *
     * @param cbDocLink the configured link template
     * @param materialNumber the main equipment's material number
     * @returns the link, or `''` when the template has no placeholder
     */
    private getCbDocUrl(cbDocLink: string, materialNumber: string): string {
        if (!cbDocLink.includes('materialNumber')) {
            this.log.error(
                `NotificationDetailsContentFactory: CB-DOC link : ERROR : 'materialNumber' string not found in cbDocLink - ${cbDocLink}`,
            );
            return '';
        }
        return cbDocLink.replace('materialNumber', materialNumber);
    }

    private toParticipantItem(participant: ParticipantModel): PaceNotificationParticipantItem {
        const lastName = participant.lastName.value;
        const firstName = participant.firstName.value;
        return {
            kind: 'participant',
            id: `${participant.pos.value}`,
            name: lastName && firstName ? `${lastName}, ${firstName}` : `${lastName}${firstName}`,
            email: participant.mail.value,
        };
    }

    private toMaterialItem(state: NotificationDetailState, material: MaterialModel): PaceNotificationMaterialItem {
        const pickedUpQuantity = MaterialService.pickedUpQuantityFromLocalStorage(material);
        const materialNumber = material.materialIdentifier.materialNumber.value;
        const sparePartCatalogLink = state.mainEquipment
            ? this.materialUtilitiesService.getSparePartCatalogMatLink(state.mainEquipment.matnr)
            : '';

        return {
            kind: 'material',
            id: `${material.materialIdentifier.orderNumber.value}:${material.materialIdentifier.orderPosition.value}:${material.materialKey}`,
            headline: `${material.materialIdentifier.orderNumber.value} | ${material.materialIdentifier.orderPosition.value} | ${material.deliveredQuantity} ${material.quantityUnit.value}`,
            isDangerousGoods: material.showDangerousInfo,
            isTaxiBillable: material.isPurchaseOrderTaxiBillable,
            isQualityPart: material.isQualityPart,
            isRentalTool: material.isRentalTool,
            description: material.description.value,
            materialNumber,
            trackOrderUrl: material.csmlTrackerUrl,
            trackOrderLabel: <string>this.translate.instant('TRACK_ORDER'),
            smartSensorUrl:
                material.smartSensorTrackerUrl.length > 0
                    ? `${material.smartSensorTrackerUrl}${material.materialIdentifier.orderNumber.value}`
                    : '',
            smartSensorAriaLabel: <string>(
                this.translate.instant('NOTIFICATION_DETAILS_LINK_TO_SMART_SENSOR_TRACKER_LABEL')
            ),
            sparePartCatalogUrl: sparePartCatalogLink ? `${sparePartCatalogLink}&partnum=${materialNumber}` : '',
            sparePartCatalogAriaLabel: <string>(
                this.translate.instant('NOTIFICATION_DETAILS_LINK_TO_SPARE_PARTS_CATALOG_LABEL')
            ),
            pickedUpText: pickedUpQuantity
                ? `${pickedUpQuantity} ${<string>this.translate.instant('MATERIAL_PICKUP_PICKED_STATUS')}`
                : '',
            isNotReceived: material.materialStatus.isNotReceived,
            pickupLabel: <string>this.translate.instant('NOTIFICATION_DETAILS_MATERIAL_PICKUP_LABEL'),
            notReceivedLabel: <string>this.translate.instant('NOTIFICATION_DETAILS_MATERIAL_NOT_RECEIVED_LABEL'),
        };
    }

    /**
     * Arranges the items into slides of columns, filling each column to `itemsPerColumn` before
     * starting the next and each slide to `columnsPerSlide` before starting the next.
     *
     * @param items the items to lay out, in the order they are shown
     * @param columnsPerSlide how many columns fit on one slide
     * @param itemsPerColumn how many items one column holds
     * @returns the slides, each a list of columns of items
     */
    private toSlides(
        items: PaceNotificationCarouselItem[],
        columnsPerSlide: number,
        itemsPerColumn: number,
    ): PaceNotificationCarouselItem[][][] {
        if (items.length === 0 || !columnsPerSlide) {
            return [];
        }
        const itemsPerSlide = columnsPerSlide * itemsPerColumn;
        const slides: PaceNotificationCarouselItem[][][] = [];

        for (let start = 0; start < items.length; start += itemsPerSlide) {
            const slideItems = items.slice(start, start + itemsPerSlide);
            const columns: PaceNotificationCarouselItem[][] = [];
            for (let column = 0; column < slideItems.length; column += itemsPerColumn) {
                columns.push(slideItems.slice(column, column + itemsPerColumn));
            }
            slides.push(columns);
        }
        return slides;
    }

    private getCsmlTrackerUrl(state: NotificationDetailState): string {
        return this.uriUtilities.getCSMLTrackerURL(
            state.materialOrderNumbers.map((orderNumber) => ({ orderNumber: { value: orderNumber } })),
            this.csmlTrackerLink(),
            false,
            this.useCsmlUrlWithParam(),
        );
    }
}
